import type {
    BusinessSettings,
    CarWashState,
    Customer,
    DiscountType,
    Invoice,
    Job,
    JobEvent,
    JobStatus,
    MessageLog,
    PaymentMethod,
    Service,
    ServiceIcon,
    Staff,
    StaffDuty,
    Vehicle
} from '@/types'

import { ACTIVE_JOB_STATUSES, WORKING_JOB_STATUSES } from '@/constants/carwash'
import { calculateTotals, getActivePayment } from '@/lib/carwash/billing'
import { roundMoney } from '@/lib/carwash/format'
import { formatInvoiceNumber } from '@/lib/carwash/ids'
import { cleanPhone, samePhone } from '@/lib/carwash/phone'
import { normalizePlate, plateLabel } from '@/lib/carwash/plate'

/*
 * The engine holds every business rule of the car wash. Each command is a
 * plain object, and applyCommand turns (state, command, now) into the next
 * state, or throws a CarWashError with a message that is safe to show to the
 * operator. It never touches storage or the UI, so the same commands can be
 * sent to a real backend later without changing any screen.
 */

export class CarWashError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'CarWashError'
    }
}

export type CustomServiceInput = {
    name: string
    price: number
    durationMinutes: number
}

export type RegisterJobInput = {
    jobId: string
    vehicleId: string
    customerId: string
    plate: string
    vehicleModel: string | null
    customerName: string | null
    customerPhone: string | null
    photo: string | null
    serviceId: string | null
    customService: CustomServiceInput | null
    staffId: string | null
    allowBusyStaff: boolean
}

export type ServiceInput = {
    name: string
    description: string
    price: number
    durationMinutes: number
    icon: ServiceIcon
}

export type SettingsInput = Partial<Omit<BusinessSettings, 'updatedAt'>>

export type Command =
    | { type: 'job/register', input: RegisterJobInput }
    | { type: 'job/assign', jobId: string, staffId: string, start: boolean, allowBusyStaff: boolean }
    | { type: 'job/unassign', jobId: string }
    | { type: 'job/start', jobId: string }
    | { type: 'job/quality-check', jobId: string }
    | { type: 'job/rework', jobId: string }
    | { type: 'job/complete', jobId: string, invoiceId: string }
    | { type: 'job/cancel', jobId: string }
    | { type: 'job/photo', jobId: string, photo: string | null }
    | { type: 'job/vehicle', jobId: string, plate: string, vehicleModel: string | null, vehicleId: string, owner?: { name: string | null, phone: string | null, customerId: string } }
    | { type: 'invoice/discount', invoiceId: string, discountType: DiscountType, discountValue: number }
    | { type: 'invoice/pay', invoiceId: string, method: PaymentMethod, paymentId: string }
    | { type: 'invoice/unpay', invoiceId: string }
    | { type: 'staff/create', staffId: string, name: string, phone: string | null, photo: string | null }
    | { type: 'staff/update', staffId: string, name: string, phone: string | null, photo: string | null }
    | { type: 'staff/duty', staffId: string, duty: StaffDuty }
    | { type: 'service/create', serviceId: string, service: ServiceInput }
    | { type: 'service/update', serviceId: string, service: ServiceInput }
    | { type: 'service/active', serviceId: string, active: boolean }
    | { type: 'vehicle/update', vehicleId: string, model: string | null, ownerName: string | null, ownerPhone: string | null, customerId: string }
    | { type: 'settings/update', settings: SettingsInput }
    | { type: 'message/log', message: MessageLog }

/* Helpers ----------------------------------------------------------- */

export const isActiveJob = (job: Pick<Job, 'status'>): boolean => ACTIVE_JOB_STATUSES.includes(job.status)

export const isWorkingJob = (job: Pick<Job, 'status'>): boolean => WORKING_JOB_STATUSES.includes(job.status)

const cleanText = (value: string | null | undefined, max = 80): string | null => {
    const trimmed = (value ?? '').replace(/\s+/g, ' ').trim()
    return trimmed ? trimmed.slice(0, max) : null
}

const findJob = (state: CarWashState, jobId: string): Job => {
    const job = state.jobs.find(item => item.id === jobId)
    if (!job) throw new CarWashError('This vehicle could not be found. It may have been removed.')
    return job
}

const findInvoice = (state: CarWashState, invoiceId: string): Invoice => {
    const invoice = state.invoices.find(item => item.id === invoiceId)
    if (!invoice) throw new CarWashError('This bill could not be found.')
    return invoice
}

const findStaff = (state: CarWashState, staffId: string): Staff => {
    const member = state.staff.find(item => item.id === staffId)
    if (!member) throw new CarWashError('That staff member could not be found.')
    return member
}

const findService = (state: CarWashState, serviceId: string): Service => {
    const service = state.services.find(item => item.id === serviceId)
    if (!service) throw new CarWashError('That service could not be found.')
    return service
}

const withJob = (state: CarWashState, job: Job): CarWashState => ({
    ...state,
    jobs: state.jobs.map(item => item.id === job.id ? job : item)
})

const withInvoice = (state: CarWashState, invoice: Invoice): CarWashState => ({
    ...state,
    invoices: state.invoices.map(item => item.id === invoice.id ? invoice : item)
})

const addEvents = (job: Job, now: string, ...events: Omit<JobEvent, 'at'>[]): Job => ({
    ...job,
    events: [...job.events, ...events.map(event => ({ at: now, ...event }))],
    updatedAt: now
})

const assertStaffCanWork = (state: CarWashState, staffId: string, jobId: string | null, allowBusy: boolean): Staff => {
    const member = findStaff(state, staffId)

    if (member.duty === 'inactive') throw new CarWashError(`${member.name} is marked inactive.`)
    if (member.duty === 'on_break') throw new CarWashError(`${member.name} is on a break. End the break first.`)

    const busyWith = state.jobs.find(job => job.staffId === staffId && job.id !== jobId && isWorkingJob(job))

    if (busyWith && !allowBusy) throw new CarWashError(`${member.name} is already working on ${plateLabel(busyWith.plate)}.`)

    return member
}

const validateServiceInput = (service: ServiceInput): ServiceInput => {
    const name = cleanText(service.name, 40)
    if (!name) throw new CarWashError('Give the service a name.')

    const price = roundMoney(Number(service.price))
    if (!Number.isFinite(price) || price < 0) throw new CarWashError('Enter a valid price.')

    const durationMinutes = Math.round(Number(service.durationMinutes))
    if (!Number.isFinite(durationMinutes) || durationMinutes < 1 || durationMinutes > 600) throw new CarWashError('Enter a duration between 1 and 600 minutes.')

    return {
        name,
        description: cleanText(service.description, 160) ?? '',
        price,
        durationMinutes,
        icon: service.icon
    }
}

/*
 * Links a vehicle to an owner. Reuses the linked customer when there is one,
 * otherwise matches by phone number, otherwise creates a new customer.
 */
const upsertOwner = (
    customers: Customer[],
    currentId: string | null,
    name: string | null,
    phone: string | null,
    newId: string,
    now: string
): { customers: Customer[], customerId: string | null } => {
    if (!name && !phone) return { customers, customerId: currentId }

    const linked = currentId ? customers.find(customer => customer.id === currentId) : undefined

    if (linked) {
        const updated: Customer = { ...linked, name: name ?? linked.name, phone: phone ?? linked.phone, updatedAt: now }
        return { customers: customers.map(customer => customer.id === linked.id ? updated : customer), customerId: linked.id }
    }

    const byPhone = phone ? customers.find(customer => samePhone(customer.phone, phone)) : undefined

    if (byPhone) {
        const updated: Customer = { ...byPhone, name: name ?? byPhone.name, updatedAt: now }
        return { customers: customers.map(customer => customer.id === byPhone.id ? updated : customer), customerId: byPhone.id }
    }

    const created: Customer = { id: newId, name, phone, createdAt: now, updatedAt: now }
    return { customers: [...customers, created], customerId: created.id }
}

/* Jobs -------------------------------------------------------------- */

const registerJob = (state: CarWashState, input: RegisterJobInput, now: string): CarWashState => {
    const plate = normalizePlate(input.plate)

    if (plate) {
        const existing = state.jobs.find(job => job.plate === plate && isActiveJob(job))
        if (existing) throw new CarWashError(`${plateLabel(plate)} is already in the wash.`)
    }

    let serviceId: string | null = null
    let serviceName: string
    let price: number
    let estimatedMinutes: number

    if (input.serviceId) {
        const service = findService(state, input.serviceId)
        if (!service.active) throw new CarWashError(`${service.name} is not offered right now.`)

        serviceId = service.id
        serviceName = service.name
        price = service.price
        estimatedMinutes = service.durationMinutes
    }
    else if (input.customService) {
        serviceName = cleanText(input.customService.name, 40) ?? 'Custom Service'
        price = roundMoney(Number(input.customService.price))
        estimatedMinutes = Math.round(Number(input.customService.durationMinutes)) || 30

        if (!Number.isFinite(price) || price < 0) throw new CarWashError('Enter a valid price for the custom service.')
        if (estimatedMinutes < 1 || estimatedMinutes > 600) throw new CarWashError('Enter a duration between 1 and 600 minutes.')
    }
    else {
        throw new CarWashError('Choose a service.')
    }

    if (input.staffId) assertStaffCanWork(state, input.staffId, input.jobId, input.allowBusyStaff)

    const model = cleanText(input.vehicleModel, 40)
    const ownerName = cleanText(input.customerName, 60)
    const ownerPhone = cleanPhone(input.customerPhone)

    const existingVehicle = plate ? state.vehicles.find(vehicle => vehicle.plate === plate) : undefined
    const owner = upsertOwner(state.customers, existingVehicle?.customerId ?? null, ownerName, ownerPhone, input.customerId, now)

    let vehicle: Vehicle
    let vehicles: Vehicle[]

    if (existingVehicle) {
        vehicle = {
            ...existingVehicle,
            model: model ?? existingVehicle.model,
            customerId: owner.customerId,
            photo: input.photo ?? existingVehicle.photo,
            updatedAt: now
        }
        vehicles = state.vehicles.map(item => item.id === vehicle.id ? vehicle : item)
    }
    else {
        vehicle = {
            id: input.vehicleId,
            plate,
            model,
            customerId: owner.customerId,
            photo: input.photo,
            createdAt: now,
            updatedAt: now
        }
        vehicles = [...state.vehicles, vehicle]
    }

    const startNow = Boolean(input.staffId)

    const events: JobEvent[] = [{ at: now, type: 'registered' }]

    if (startNow) {
        events.push({ at: now, type: 'assigned', staffId: input.staffId })
        events.push({ at: now, type: 'status', status: 'washing' })
    }

    const job: Job = {
        id: input.jobId,
        number: state.nextJobNumber,
        vehicleId: vehicle.id,
        plate,
        vehicleModel: vehicle.model,
        customerId: owner.customerId,
        photo: input.photo,
        serviceId,
        serviceName,
        price,
        estimatedMinutes,
        staffId: input.staffId,
        status: startNow ? 'washing' : 'waiting',
        enteredAt: now,
        assignedAt: startNow ? now : null,
        startedAt: startNow ? now : null,
        qualityCheckAt: null,
        completedAt: null,
        cancelledAt: null,
        invoiceId: null,
        events,
        createdAt: now,
        updatedAt: now
    }

    return {
        ...state,
        vehicles,
        customers: owner.customers,
        jobs: [...state.jobs, job],
        nextJobNumber: state.nextJobNumber + 1
    }
}

const assignJob = (
    state: CarWashState,
    command: Extract<Command, { type: 'job/assign' }>,
    now: string
): CarWashState => {
    const job = findJob(state, command.jobId)

    if (!isActiveJob(job)) throw new CarWashError('This vehicle is no longer in the wash.')

    if (job.staffId === command.staffId) {
        return command.start && job.status === 'assigned' ? startJob(state, job.id, now) : state
    }

    assertStaffCanWork(state, command.staffId, job.id, command.allowBusyStaff)

    if (job.status === 'waiting') {
        const status: JobStatus = command.start ? 'washing' : 'assigned'
        const next = addEvents(
            {
                ...job,
                staffId: command.staffId,
                status,
                assignedAt: now,
                startedAt: command.start ? now : null
            },
            now,
            { type: 'assigned', staffId: command.staffId },
            { type: 'status', status }
        )

        return withJob(state, next)
    }

    let next = addEvents(
        { ...job, staffId: command.staffId },
        now,
        { type: 'reassigned', staffId: command.staffId, fromStaffId: job.staffId }
    )

    if (command.start && next.status === 'assigned') {
        next = addEvents({ ...next, status: 'washing', startedAt: now }, now, { type: 'status', status: 'washing' })
    }

    return withJob(state, next)
}

const unassignJob = (state: CarWashState, jobId: string, now: string): CarWashState => {
    const job = findJob(state, jobId)

    if (job.status !== 'assigned') throw new CarWashError('Only vehicles that have not started washing can go back to the queue.')

    const next = addEvents(
        { ...job, staffId: null, status: 'waiting', assignedAt: null },
        now,
        { type: 'unassigned', fromStaffId: job.staffId },
        { type: 'status', status: 'waiting' }
    )

    return withJob(state, next)
}

const startJob = (state: CarWashState, jobId: string, now: string): CarWashState => {
    const job = findJob(state, jobId)

    if (job.status !== 'assigned') throw new CarWashError('Assign a staff member before starting the wash.')
    if (!job.staffId) throw new CarWashError('Assign a staff member before starting the wash.')

    const member = findStaff(state, job.staffId)
    if (member.duty === 'inactive') throw new CarWashError(`${member.name} is marked inactive. Change the staff member first.`)

    const next = addEvents({ ...job, status: 'washing', startedAt: now }, now, { type: 'status', status: 'washing' })

    return withJob(state, next)
}

const sendToQualityCheck = (state: CarWashState, jobId: string, now: string): CarWashState => {
    const job = findJob(state, jobId)

    if (job.status !== 'washing') throw new CarWashError('Only vehicles that are washing can move to quality check.')

    const next = addEvents({ ...job, status: 'quality_check', qualityCheckAt: now }, now, { type: 'status', status: 'quality_check' })

    return withJob(state, next)
}

const sendBackToWashing = (state: CarWashState, jobId: string, now: string): CarWashState => {
    const job = findJob(state, jobId)

    if (job.status !== 'quality_check') throw new CarWashError('Only vehicles in quality check can go back to washing.')

    const next = addEvents({ ...job, status: 'washing' }, now, { type: 'status', status: 'washing', note: 'Sent back for rework' })

    return withJob(state, next)
}

const completeJob = (state: CarWashState, jobId: string, invoiceId: string, now: string): CarWashState => {
    const job = findJob(state, jobId)

    if (job.status === 'completed') throw new CarWashError('This wash is already completed.')
    if (job.status !== 'washing' && job.status !== 'quality_check') throw new CarWashError('Start the wash before completing it.')

    const vehicle = state.vehicles.find(item => item.id === job.vehicleId)
    const lines = [{ label: job.serviceName, amount: job.price }]
    const totals = calculateTotals(lines, 'amount', 0)
    const settings = state.settings

    const invoice: Invoice = {
        id: invoiceId,
        number: formatInvoiceNumber(settings.invoicePrefix, settings.nextInvoiceNumber),
        jobId: job.id,
        vehicleId: job.vehicleId,
        customerId: job.customerId ?? vehicle?.customerId ?? null,
        plate: job.plate,
        vehicleModel: job.vehicleModel,
        serviceName: job.serviceName,
        lines,
        subtotal: totals.subtotal,
        discountType: 'amount',
        discountValue: 0,
        discountAmount: totals.discountAmount,
        total: totals.total,
        status: 'pending',
        paymentMethod: null,
        issuedAt: now,
        paidAt: null,
        createdAt: now,
        updatedAt: now
    }

    const next = addEvents(
        { ...job, status: 'completed', completedAt: now, invoiceId, customerId: invoice.customerId },
        now,
        { type: 'completed', staffId: job.staffId }
    )

    return {
        ...withJob(state, next),
        invoices: [...state.invoices, invoice],
        settings: { ...settings, nextInvoiceNumber: settings.nextInvoiceNumber + 1, updatedAt: now }
    }
}

const cancelJob = (state: CarWashState, jobId: string, now: string): CarWashState => {
    const job = findJob(state, jobId)

    if (!isActiveJob(job)) throw new CarWashError('Only vehicles still in the wash can be cancelled.')

    const next = addEvents({ ...job, status: 'cancelled', cancelledAt: now }, now, { type: 'cancelled', staffId: job.staffId })

    return withJob(state, next)
}

const setJobPhoto = (state: CarWashState, jobId: string, photo: string | null, now: string): CarWashState => {
    const job = findJob(state, jobId)
    const next = addEvents({ ...job, photo }, now, { type: 'edited', note: photo ? 'Photo updated' : 'Photo removed' })
    const vehicles = state.vehicles.map(vehicle => vehicle.id === job.vehicleId && photo
        ? { ...vehicle, photo, updatedAt: now }
        : vehicle)

    return { ...withJob(state, next), vehicles }
}

/*
 * Corrects the number plate or model of a visit. When the corrected number
 * belongs to a vehicle we already know, the visit moves into that vehicle's
 * history so returning customers stay recognisable.
 */
const updateJobVehicle = (state: CarWashState, command: Extract<Command, { type: 'job/vehicle' }>, now: string): CarWashState => {
    const job = findJob(state, command.jobId)
    const plate = normalizePlate(command.plate)
    const model = cleanText(command.vehicleModel, 40)
    const current = state.vehicles.find(vehicle => vehicle.id === job.vehicleId)

    if (plate && plate !== job.plate) {
        const clash = state.jobs.find(item => item.id !== job.id && item.plate === plate && isActiveJob(item))
        if (clash && isActiveJob(job)) throw new CarWashError(`${plateLabel(plate)} is already in the wash.`)
    }

    const otherVisits = state.jobs.filter(item => item.vehicleId === job.vehicleId && item.id !== job.id)
    const match = plate ? state.vehicles.find(vehicle => vehicle.plate === plate && vehicle.id !== job.vehicleId) : undefined

    let vehicles = state.vehicles
    let vehicleId = job.vehicleId

    if (match) {
        vehicleId = match.id
        vehicles = vehicles.map(vehicle => vehicle.id === match.id
            ? { ...vehicle, model: model ?? vehicle.model, photo: job.photo ?? vehicle.photo, updatedAt: now }
            : vehicle)

        if (!otherVisits.length) vehicles = vehicles.filter(vehicle => vehicle.id !== job.vehicleId)
    }
    else if (current && (otherVisits.length === 0 || current.plate === plate)) {
        vehicles = vehicles.map(vehicle => vehicle.id === current.id
            ? { ...vehicle, plate, model: model ?? vehicle.model, updatedAt: now }
            : vehicle)
    }
    else {
        const created: Vehicle = {
            id: command.vehicleId,
            plate,
            model: model ?? current?.model ?? null,
            customerId: current?.customerId ?? null,
            photo: job.photo,
            createdAt: now,
            updatedAt: now
        }

        vehicleId = created.id
        vehicles = [...vehicles, created]
    }

    let customers = state.customers
    let linked = vehicles.find(vehicle => vehicle.id === vehicleId)

    if (command.owner && linked) {
        const ownerName = cleanText(command.owner.name, 60)
        const ownerPhone = cleanPhone(command.owner.phone)
        const linkedOwnerId = linked.customerId
        const existingOwner = linkedOwnerId ? customers.find(customer => customer.id === linkedOwnerId) : undefined
        let ownerId: string | null = null

        if (existingOwner && (ownerName || ownerPhone)) {
            customers = customers.map(customer => customer.id === existingOwner.id
                ? { ...customer, name: ownerName, phone: ownerPhone, updatedAt: now }
                : customer)
            ownerId = existingOwner.id
        }
        else if (ownerName || ownerPhone) {
            const owner = upsertOwner(customers, null, ownerName, ownerPhone, command.owner.customerId, now)
            customers = owner.customers
            ownerId = owner.customerId
        }

        const withOwner: Vehicle = { ...linked, customerId: ownerId, updatedAt: now }
        vehicles = vehicles.map(vehicle => vehicle.id === withOwner.id ? withOwner : vehicle)
        linked = withOwner
    }

    const vehicleModel = model ?? linked?.model ?? null
    const customerId = command.owner ? linked?.customerId ?? null : linked?.customerId ?? job.customerId

    const next = addEvents(
        { ...job, vehicleId, plate, vehicleModel, customerId },
        now,
        { type: 'edited', note: 'Vehicle details updated' }
    )

    const invoices = state.invoices.map(invoice => invoice.jobId === job.id
        ? { ...invoice, vehicleId, plate, vehicleModel, customerId, updatedAt: now }
        : invoice)

    return { ...withJob({ ...state, vehicles, customers }, next), invoices }
}

/* Billing ----------------------------------------------------------- */

const applyDiscount = (state: CarWashState, command: Extract<Command, { type: 'invoice/discount' }>, now: string): CarWashState => {
    const invoice = findInvoice(state, command.invoiceId)

    if (invoice.status === 'paid') throw new CarWashError('This bill is already paid. Mark it as pending to change the discount.')

    const value = Math.max(0, roundMoney(Number(command.discountValue)))
    if (!Number.isFinite(value)) throw new CarWashError('Enter a valid discount.')
    if (command.discountType === 'percent' && value > 100) throw new CarWashError('A discount cannot be more than 100%.')
    if (command.discountType === 'amount' && value > invoice.subtotal) throw new CarWashError('The discount cannot be more than the bill amount.')

    const totals = calculateTotals(invoice.lines, command.discountType, value)
    const next: Invoice = {
        ...invoice,
        discountType: command.discountType,
        discountValue: value,
        discountAmount: totals.discountAmount,
        total: totals.total,
        updatedAt: now
    }

    const job = state.jobs.find(item => item.id === invoice.jobId)
    const updated = withInvoice(state, next)

    const note = value ? `${command.discountType}:${value}` : 'removed'

    return job ? withJob(updated, addEvents(job, now, { type: 'discount', note })) : updated
}

const payInvoice = (state: CarWashState, command: Extract<Command, { type: 'invoice/pay' }>, now: string): CarWashState => {
    const invoice = findInvoice(state, command.invoiceId)

    if (invoice.status === 'paid') throw new CarWashError('This bill is already paid.')

    const next: Invoice = { ...invoice, status: 'paid', paymentMethod: command.method, paidAt: now, updatedAt: now }
    const payment = {
        id: command.paymentId,
        invoiceId: invoice.id,
        amount: invoice.total,
        method: command.method,
        receivedAt: now,
        voidedAt: null,
        createdAt: now
    }

    const job = state.jobs.find(item => item.id === invoice.jobId)
    const updated = { ...withInvoice(state, next), payments: [...state.payments, payment] }

    return job ? withJob(updated, addEvents(job, now, { type: 'paid', note: command.method })) : updated
}

const unpayInvoice = (state: CarWashState, invoiceId: string, now: string): CarWashState => {
    const invoice = findInvoice(state, invoiceId)

    if (invoice.status !== 'paid') throw new CarWashError('This bill is not paid yet.')

    const active = getActivePayment(state.payments, invoice.id)
    const next: Invoice = { ...invoice, status: 'pending', paymentMethod: null, paidAt: null, updatedAt: now }
    const payments = state.payments.map(payment => payment.id === active?.id ? { ...payment, voidedAt: now } : payment)

    const job = state.jobs.find(item => item.id === invoice.jobId)
    const updated = { ...withInvoice(state, next), payments }

    return job ? withJob(updated, addEvents(job, now, { type: 'unpaid' })) : updated
}

/* Staff ------------------------------------------------------------- */

const createStaff = (state: CarWashState, command: Extract<Command, { type: 'staff/create' }>, now: string): CarWashState => {
    const name = cleanText(command.name, 40)
    if (!name) throw new CarWashError('Enter the staff member name.')

    const member: Staff = {
        id: command.staffId,
        name,
        phone: cleanPhone(command.phone),
        photo: command.photo,
        duty: 'on_duty',
        dutySince: now,
        createdAt: now,
        updatedAt: now
    }

    return { ...state, staff: [...state.staff, member] }
}

const updateStaff = (state: CarWashState, command: Extract<Command, { type: 'staff/update' }>, now: string): CarWashState => {
    const member = findStaff(state, command.staffId)
    const name = cleanText(command.name, 40)
    if (!name) throw new CarWashError('Enter the staff member name.')

    const next: Staff = { ...member, name, phone: cleanPhone(command.phone), photo: command.photo, updatedAt: now }

    return { ...state, staff: state.staff.map(item => item.id === next.id ? next : item) }
}

const setStaffDuty = (state: CarWashState, staffId: string, duty: StaffDuty, now: string): CarWashState => {
    const member = findStaff(state, staffId)

    if (member.duty === duty) return state

    if (duty === 'inactive') {
        const working = state.jobs.find(job => job.staffId === staffId && isWorkingJob(job))
        if (working) throw new CarWashError(`${member.name} is working on ${plateLabel(working.plate)}. Complete or reassign it first.`)
    }

    const next: Staff = { ...member, duty, dutySince: now, updatedAt: now }

    return { ...state, staff: state.staff.map(item => item.id === next.id ? next : item) }
}

/* Services ---------------------------------------------------------- */

const createService = (state: CarWashState, serviceId: string, input: ServiceInput, now: string): CarWashState => {
    const service: Service = { id: serviceId, ...validateServiceInput(input), active: true, createdAt: now, updatedAt: now }
    return { ...state, services: [...state.services, service] }
}

const updateService = (state: CarWashState, serviceId: string, input: ServiceInput, now: string): CarWashState => {
    const service = findService(state, serviceId)
    const next: Service = { ...service, ...validateServiceInput(input), updatedAt: now }
    return { ...state, services: state.services.map(item => item.id === next.id ? next : item) }
}

const setServiceActive = (state: CarWashState, serviceId: string, active: boolean, now: string): CarWashState => {
    const service = findService(state, serviceId)

    if (!active && service.active && state.services.filter(item => item.active).length <= 1) {
        throw new CarWashError('Keep at least one service active.')
    }

    const next: Service = { ...service, active, updatedAt: now }
    return { ...state, services: state.services.map(item => item.id === next.id ? next : item) }
}

/* Vehicles and customers -------------------------------------------- */

const updateVehicle = (state: CarWashState, command: Extract<Command, { type: 'vehicle/update' }>, now: string): CarWashState => {
    const vehicle = state.vehicles.find(item => item.id === command.vehicleId)
    if (!vehicle) throw new CarWashError('This vehicle could not be found.')

    const model = cleanText(command.model, 40)
    const ownerName = cleanText(command.ownerName, 60)
    const ownerPhone = cleanPhone(command.ownerPhone)

    let customers = state.customers
    let customerId: string | null = null

    if (ownerName || ownerPhone) {
        const linked = vehicle.customerId ? customers.find(customer => customer.id === vehicle.customerId) : undefined

        if (linked) {
            customers = customers.map(customer => customer.id === linked.id
                ? { ...customer, name: ownerName, phone: ownerPhone, updatedAt: now }
                : customer)
            customerId = linked.id
        }
        else {
            const owner = upsertOwner(customers, null, ownerName, ownerPhone, command.customerId, now)
            customers = owner.customers
            customerId = owner.customerId
        }
    }

    const next: Vehicle = { ...vehicle, model, customerId, updatedAt: now }

    const jobs = state.jobs.map(job => job.vehicleId === vehicle.id && isActiveJob(job)
        ? { ...job, vehicleModel: model, customerId, updatedAt: now }
        : job)

    return {
        ...state,
        customers,
        jobs,
        vehicles: state.vehicles.map(item => item.id === next.id ? next : item)
    }
}

/* Settings and messages --------------------------------------------- */

const updateSettings = (state: CarWashState, input: SettingsInput, now: string): CarWashState => {
    const next: BusinessSettings = { ...state.settings, ...input, updatedAt: now }

    next.name = cleanText(next.name, 60) ?? ''
    if (!next.name) throw new CarWashError('Enter the business name.')

    next.invoicePrefix = (next.invoicePrefix ?? '').replace(/\s+/g, '').slice(0, 8)
    next.nextInvoiceNumber = Math.max(1, Math.floor(Number(next.nextInvoiceNumber) || 1))
    next.countryCode = (next.countryCode ?? '').replace(/\D/g, '').slice(0, 4) || '91'

    const used = state.invoices.some(invoice => invoice.number === formatInvoiceNumber(next.invoicePrefix, next.nextInvoiceNumber))
    if (used) throw new CarWashError('That invoice number is already used. Choose a higher next number.')

    return { ...state, settings: next }
}

const logMessage = (state: CarWashState, message: MessageLog, now: string): CarWashState => {
    const messages = [...state.messages, message].slice(-300)
    const job = message.jobId ? state.jobs.find(item => item.id === message.jobId) : undefined
    const updated = { ...state, messages }

    return job ? withJob(updated, addEvents(job, now, { type: 'message', note: message.template })) : updated
}

/* Entry point ------------------------------------------------------- */

export const applyCommand = (state: CarWashState, command: Command, now: string): CarWashState => {
    switch (command.type) {
        case 'job/register': return registerJob(state, command.input, now)
        case 'job/assign': return assignJob(state, command, now)
        case 'job/unassign': return unassignJob(state, command.jobId, now)
        case 'job/start': return startJob(state, command.jobId, now)
        case 'job/quality-check': return sendToQualityCheck(state, command.jobId, now)
        case 'job/rework': return sendBackToWashing(state, command.jobId, now)
        case 'job/complete': return completeJob(state, command.jobId, command.invoiceId, now)
        case 'job/cancel': return cancelJob(state, command.jobId, now)
        case 'job/photo': return setJobPhoto(state, command.jobId, command.photo, now)
        case 'job/vehicle': return updateJobVehicle(state, command, now)
        case 'invoice/discount': return applyDiscount(state, command, now)
        case 'invoice/pay': return payInvoice(state, command, now)
        case 'invoice/unpay': return unpayInvoice(state, command.invoiceId, now)
        case 'staff/create': return createStaff(state, command, now)
        case 'staff/update': return updateStaff(state, command, now)
        case 'staff/duty': return setStaffDuty(state, command.staffId, command.duty, now)
        case 'service/create': return createService(state, command.serviceId, command.service, now)
        case 'service/update': return updateService(state, command.serviceId, command.service, now)
        case 'service/active': return setServiceActive(state, command.serviceId, command.active, now)
        case 'vehicle/update': return updateVehicle(state, command, now)
        case 'settings/update': return updateSettings(state, command.settings, now)
        case 'message/log': return logMessage(state, command.message, now)
        default: return state
    }
}
